import React, { useCallback, useMemo } from "react";
import { LightTooltip, RewardToolTipWrapper } from "@smartrr/shared/customerPortal/components/AquariusToolTip";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { LoyaltyStoreAccess } from "../../store";
import {
  isRewardItemInUpcomingOrder,
  checkPurchaseStatesInactiveWithPaused,
  getNearestUnpauseDate,
  isRewardIncentive,
} from "../../utils";
import { usePurchaseStates } from "../../../../hooks/usePurchaseStates";
import { useOpenModal } from "@customer-app/state/actionCreators/modal";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { useIsOtpCustomer } from "../../../../hooks/useIsOtpCustomer";
import { prefixClassName } from "@smartrr/shared/components/primitives";

const ToolTipButton = React.forwardRef<HTMLDivElement>(({ ...props }, ref) => {
  return (
    <RewardToolTipWrapper {...props} ref={ref}>
      <AquariusButton
        disabled={true}
        sx={{ height: "30px", marginTop: "0px", padding: "6px 18px" }}
        style={{ pointerEvents: "all" }} // needed to allow hover events in tests when disabled = true
      >
        {typedLocalize("loyalty.redeem")}
      </AquariusButton>
    </RewardToolTipWrapper>
  );
});

export const RedeemButton = ({ item }: { item: LoyaltyApi.VariantOrIncentive.Type }) => {
  const upcomingOrder = LoyaltyStoreAccess.useOrder();
  const settings = LoyaltyStoreAccess.useRewardSettings();
  const isOTPEnabled = LoyaltyStoreAccess.useIsOtpEnabled();
  const isOTPCustomer = useIsOtpCustomer();

  const purchaseStates = usePurchaseStates();
  const openModal = useOpenModal();

  const hasRedemptionLimit = useMemo(() => !!(settings?.limitUse && settings.limitUse > 0), [settings]);
  const checkedItem = useMemo(() => {
    if (!upcomingOrder) {
      return false;
    }
    return isRewardItemInUpcomingOrder(upcomingOrder, item);
  }, [upcomingOrder, item]);

  const redemptionLimitReached = useMemo(
    () => hasRedemptionLimit && checkedItem,
    [hasRedemptionLimit, checkedItem]
  );

  const onlyPausedPurchaseStates = useMemo(
    () => checkPurchaseStatesInactiveWithPaused(purchaseStates),
    [purchaseStates]
  );
  const pausedPurchaseStates = useMemo(
    () => purchaseStates.filter(purchaseState => purchaseState.purchaseStateStatus === "PAUSED"),
    [purchaseStates]
  );
  const nearestPausedPurchaseState = useMemo(
    () => getNearestUnpauseDate(pausedPurchaseStates),
    [pausedPurchaseStates]
  );
  const isItemInNearestPausedPurchaseState = useMemo(() => {
    if (!onlyPausedPurchaseStates) {
      return false;
    }
    const hasDiscounts = nearestPausedPurchaseState && nearestPausedPurchaseState?.discounts?.length > 0;
    const isIncentiveReward = isRewardIncentive(item);

    if (hasDiscounts && isIncentiveReward) {
      return true;
    }

    if (item.rewardType === "PRODUCT") {
      return nearestPausedPurchaseState?.stLineItems.some(
        lineItem => lineItem.isRedeemed && lineItem.vnt.id === item.variantId
      );
    }
    return false;
  }, [onlyPausedPurchaseStates, nearestPausedPurchaseState, item]);

  const openModalAction = useCallback(() => {
    if (isRewardIncentive(item)) {
      openModal("REDEEM_INCENTIVE", { incentive: item });
    } else {
      openModal("REDEEM_PRODUCT", { variant: item });
    }
  }, []);

  const canRewardBeRedeemed = LoyaltyStoreAccess.useIsRewardRedeemable(item.id);
  const showSubscriberExclusiveBadge = useMemo(
    () => isOTPCustomer && item.subscriberOnly && isOTPEnabled,
    [isOTPCustomer, item, isOTPEnabled]
  );

  const isDisabled = redemptionLimitReached || showSubscriberExclusiveBadge || !canRewardBeRedeemed;
  const isItemRedeemedInPausedCPS = hasRedemptionLimit && isItemInNearestPausedPurchaseState;

  if (redemptionLimitReached || isItemRedeemedInPausedCPS) {
    const alreadyRedeemedText = isRewardIncentive(item)
      ? typedLocalize("loyalty.incentiveAlreadyRedeemedTooltip")
      : typedLocalize("loyalty.alreadyRedeemedTooltip");
    return (
      <LightTooltip title={alreadyRedeemedText}>
        <ToolTipButton />
      </LightTooltip>
    );
  }

  return (
    <AquariusButton
      disabled={isDisabled}
      sx={{ height: "30px" }}
      onClick={openModalAction}
      className={prefixClassName("loyalty-reward-item-redeem-button")}
      style={{ pointerEvents: "all" }} // needed to allow hover events in tests when disabled = true
    >
      {typedLocalize("loyalty.redeem")}
    </AquariusButton>
  );
};
